// WASM loading strategies — separated so bundlers can tree-shake node:fs.
//
// This module provides the browser-safe loading path. The Node.js-specific
// sync loader is in wasm-loader-node.ts.
//
// THIS MODULE IS GLUE-TAINTED BY DESIGN. The static import below drags
// ../pkg/layers_core.js — and therefore wasm-pack's `require("url")` binary
// locator — into whatever imports it. Only the node entry (index.ts) and the
// browser entry (browser.ts) may reach it. The react-native entry MUST NOT:
// Metro cannot resolve `url` and the whole app fails to bundle. The readiness
// flag and the constructor registry live in wasm-state.ts precisely so that
// core.ts can stay on the clean side of that line.
//
// `init` is the wasm-bindgen default export (`__wbg_init`). It MUST be reached
// through this static import and never through `await import('../pkg/...')` —
// see the comment on initWasm() for the failure that cost us every top-level-
// await web app.
import init, { WasmLayersCore, initSync } from '../pkg/layers_core.js';

import { isWasmReady, markWasmReady, registerWasmCtor } from './wasm-state.js';

export { WasmLayersCore };
export { isWasmReady } from './wasm-state.js';

/**
 * Report a WASM load failure exactly once per process.
 *
 * NEVER make this silent again. A swallowed exception here does not stop the
 * SDK — LayersCore quietly drops to the JS engine and keeps working — so the
 * only evidence a customer or a smoke test ever gets is the `engine/js` token
 * on the wire. That token says a downgrade happened; it cannot say why. The
 * TDZ ReferenceError described on initWasm() sat behind a bare `catch {}` and
 * was invisible locally for that exact reason.
 *
 * Warn-once keeps a retried init (or a test suite that deliberately forces the
 * fallback) from spamming the console while still surfacing the cause.
 *
 * @internal
 */
let warnedWasmUnavailable = false;
export function warnWasmUnavailable(err: unknown): void {
  if (warnedWasmUnavailable) return;
  warnedWasmUnavailable = true;
  const reason =
    err instanceof Error ? `${err.name}: ${err.message}` : `${String(err)} (non-Error throw)`;
  console.warn(
    `[Layers] WASM core unavailable — running on the JS engine. Reason: ${reason}. ` +
      'Check that layers_core_bg.wasm is emitted by your bundler and served with ' +
      'Content-Type: application/wasm, and that CSP allows it.'
  );
}

/** Options for {@link initWasm}. */
export interface InitWasmOptions {
  /**
   * Absolute URL of `layers_core_bg.wasm`, overriding the glue's own
   * `new URL('layers_core_bg.wasm', import.meta.url)`.
   *
   * WHY THIS EXISTS
   *
   * `import.meta` is an ES-module construct. It does not exist in a classic
   * script, and every bundler that targets one substitutes an empty object for
   * it — esbuild says so out loud on every CDN build:
   *
   *   "import.meta" is not available with the "iife" output format and will be
   *   empty [empty-import-meta]
   *
   * So the shipped `packages/client/cdn/layers.min.js` contained
   * `new URL("layers_core_bg.wasm", $l.url)` with `$l = {}`. `undefined` is not
   * a valid base, `new URL` threw `TypeError: Invalid URL`, and the SDK ran the
   * JS engine on every page that loaded the SDK from cdn.layers.com. Measured
   * in Chrome against a served bundle: zero requests for the binary, and
   * `X-SDK-Version: client/3.2.9 core-wasm/3.2.9 engine/js` on the wire.
   *
   * A classic script CAN locate itself — `document.currentScript.src` is exactly
   * the base `import.meta.url` would have been — but only the entry that owns
   * the script tag can read it, and only while that script is still executing.
   * That is why the resolution lives in `packages/client/src/cdn.ts` and arrives
   * here as a plain string rather than being sniffed from this module.
   *
   * Also the supported escape hatch for hosting the binary somewhere other than
   * beside the bundle (`LayersConfig.wasmUrl` on @layers/client).
   */
  wasmUrl?: string;
}

/**
 * Async WASM initialization for browser environments.
 * Call this once before creating any LayersCore instances.
 * In Node.js this is not needed (sync loading is used automatically).
 *
 * DO NOT reintroduce `await import('../pkg/layers_core.js')` here.
 *
 * The glue is ALREADY in this module's static graph (the import above), so a
 * dynamic import of the same specifier bought no code splitting whatsoever —
 * and it broke WASM outright for a large class of consumers. When a module is
 * both statically and dynamically imported, Rollup stops emitting a real chunk
 * and inlines the dynamic import as `Promise.resolve().then(() => NS)`, where
 * `NS` is a module-namespace `const` it appends at the END of the chunk. In a
 * single-entry app build every module — the SDK and the consumer's own entry —
 * lands in that one chunk, and the consumer's entry body is emitted BEFORE that
 * trailing `const`.
 *
 * So an app whose entry does the completely ordinary
 *
 *   await layers.init();      // top level, ESM
 *
 * suspends the module body at that await. `const NS = ...` has not been
 * evaluated yet, initWasm() dereferences it, and the browser throws
 *
 *   ReferenceError: Cannot access 'NS' before initialization
 *
 * a temporal-dead-zone error the old bare `catch {}` swallowed whole. The
 * binary was emitted, served, and valid — it was simply never fetched, and the
 * SDK ran the JS engine on every page load without a word. Measured on the
 * published 3.2.9 build of @layers/client: zero requests for the 832KB
 * layers_core_bg.wasm that shipped to every visitor.
 *
 * It only reproduced under top-level await. The same bundle with the init call
 * inside an async IIFE lets the module body run to completion — the trailing
 * `const` initializes, the microtask resolves, and WASM loads. That is why the
 * defect survived earlier vite-preview smokes, and why a second HTML entry (a
 * chunk layout that forces a real shared chunk) appeared to "fix" it.
 *
 * Calling the static binding removes the hazard by construction: `init` is
 * bound when this module is evaluated, which is strictly before any entry body
 * runs, in every bundler and in the browser's own module loader.
 *
 * @param options.wasmUrl Where to fetch `layers_core_bg.wasm` from. Omit it and
 * the glue falls back to `new URL('layers_core_bg.wasm', import.meta.url)`,
 * which is what every ESM bundler rewrites to the emitted asset — the correct
 * default, and the one this function must keep taking when nothing is passed.
 * Pass it when `import.meta.url` cannot exist: see {@link InitWasmOptions}.
 */
export async function initWasm(options?: InitWasmOptions): Promise<void> {
  if (isWasmReady()) return;
  try {
    // `{ module_or_path }` — the object form. Handing the glue a bare string or
    // URL takes its legacy branch, which prints a "using deprecated parameters"
    // console.warn on every successful load.
    await (options?.wasmUrl === undefined ? init() : init({ module_or_path: options.wasmUrl }));
    // Register BEFORE flipping the flag: core.ts treats `isWasmReady()` as the
    // signal that a constructor is available, so the reverse order would leave
    // a window where it believes WASM is live with nothing to construct.
    registerWasmCtor(WasmLayersCore);
    markWasmReady();
  } catch (err) {
    warnWasmUnavailable(err);
  }
}

/**
 * Synchronous WASM init from a pre-loaded buffer.
 * Used by Node.js loader and tests.
 */
export function initWasmSync(module: BufferSource | WebAssembly.Module): void {
  if (isWasmReady()) return;
  try {
    initSync({ module });
    registerWasmCtor(WasmLayersCore);
    markWasmReady();
  } catch (err) {
    warnWasmUnavailable(err);
  }
}
